// src/hooks/useCredentialsDropZone.js
import { useState, useCallback } from 'react';
import { decryptCredentials } from '../crypt/cryptoUtils'; // Adjust path

export function useCredentialsDropZone(setCredentials) {
  const [isDragging, setIsDragging] = useState(false);
  const [dropError, setDropError] = useState('');

  const handleDragFileOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []); // No external dependencies needed for setIsDragging

  const handleDragFileLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    // Add a check to prevent flickering when dragging over child elements
    if (event.currentTarget.contains(event.relatedTarget)) {
        return;
    }
    setIsDragging(false);
  }, []); // No external dependencies needed for setIsDragging

  const handleDropFile = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    setDropError(''); // Clear previous errors

    const files = event.dataTransfer.files;

    if (!files || files.length === 0) {
      setDropError("No file detected.");
      return;
    }

    const file = files[0];

    if (file.type !== "application/json") {
      setDropError("Invalid file type. Please drop a .json file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      const text = e.target.result;
      try {
        const parsedData = JSON.parse(text);
        let finalCredentials = null;

        // Check for simple encrypted format first
        if (parsedData.type === "local-obfuscated" && parsedData.encryptedData && parsedData.iv) {
          console.log("Attempting to decrypt file...");
          try {
            finalCredentials = await decryptCredentials(parsedData); // Use the imported decrypt function
          } catch (decryptError) {
            console.error("Decryption failed:", decryptError);
            setDropError(decryptError.message || "Failed to decrypt file.");
            return;
          }
        }
        // Check for plain JSON format
        else if (parsedData.userId && parsedData.apiKey) {
          console.log("Processing plain JSON file...");
          finalCredentials = {
            userId: parsedData.userId,
            apiKey: parsedData.apiKey
          };
        }
        // Handle unknown format
        else {
          setDropError("JSON file format not recognized. Expected plain {userId, apiKey} or encrypted {type: 'local-obfuscated', ...}.");
          return;
        }

        // Validate and set credentials using the passed-in setter
        if (finalCredentials && typeof finalCredentials.userId === 'string' && typeof finalCredentials.apiKey === 'string') {
          setCredentials({ // Use the setter passed into the hook
            userId: finalCredentials.userId,
            apiKey: finalCredentials.apiKey,
          });
          console.log("Credentials updated successfully.");
        } else {
          setDropError("Processed data is missing 'userId' or 'apiKey'.");
        }

      } catch (parseError) {
        console.error("JSON Parsing failed:", parseError);
        setDropError("Failed to parse JSON file. Ensure it's valid JSON.");
      }
    };

    reader.onerror = (e) => {
      console.error("File reading failed:", e);
      setDropError("Failed to read the file.");
    };

    reader.readAsText(file);
  }, [setCredentials]); // Include setCredentials in the dependency array

  // Return the state and handlers needed by the component
  return {
    isDragging,
    dropError,
    handleDragFileOver,
    handleDragFileLeave,
    handleDropFile,
  };
}